import React from "react";
import { useMutation } from "@tanstack/react-query";
import { KeyRound, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhotoUploader } from "@/components/common/PhotoUploader";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { authService } from "@/services/auth.service";
import { getErrorMessage } from "@/services/api";
import { ROLE_LABELS } from "@/config/constants";

export default function ProfilePage() {
    const { user, refresh } = useAuth();

    const [name, setName] = React.useState(user?.name || "");
    const [phone, setPhone] = React.useState(user?.phone || "");
    const [photoUrl, setPhotoUrl] = React.useState(user?.photo_url || "");

    const [currentPass, setCurrentPass] = React.useState("");
    const [newPass, setNewPass] = React.useState("");
    const [confirmPass, setConfirmPass] = React.useState("");

    React.useEffect(() => {
        if (user) {
            setName(user.name || "");
            setPhone(user.phone || "");
            setPhotoUrl(user.photo_url || "");
        }
    }, [user]);

    const saveProfile = useMutation({
        mutationFn: () => authService.updateMe({ name, phone: phone || null, photo_url: photoUrl || null }),
        onSuccess: async () => {
            toast.success("Perfil atualizado.");
            await refresh();
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao atualizar perfil.")),
    });

    const changePass = useMutation({
        mutationFn: () => authService.changePassword(currentPass, newPass),
        onSuccess: () => {
            toast.success("Senha alterada com sucesso.");
            setCurrentPass("");
            setNewPass("");
            setConfirmPass("");
        },
        onError: (e) => toast.error(getErrorMessage(e, "Não foi possível alterar a senha.")),
    });

    function submitPassword(e) {
        e.preventDefault();
        if (newPass.length < 8) return toast.error("A nova senha deve ter pelo menos 8 caracteres.");
        if (newPass !== confirmPass) return toast.error("As senhas não coincidem.");
        changePass.mutate();
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Meu Perfil"
                description="Seus dados de acesso e preferências pessoais."
            />

            <div className="grid gap-6 lg:grid-cols-3">
                <Card className="rounded-xl border shadow-sm lg:col-span-2">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Dados pessoais</CardTitle>
                    </CardHeader>
                    <CardContent className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="pname">Nome</Label>
                            <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pemail">E-mail</Label>
                            <Input id="pemail" value={user?.email || ""} disabled />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pphone">Telefone</Label>
                            <Input id="pphone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Perfil</Label>
                            <div>
                                <Badge variant="secondary" className="text-[10px]">
                                    {ROLE_LABELS[user?.role] || user?.role}
                                </Badge>
                            </div>
                        </div>
                        <div className="md:col-span-2 pt-2">
                            <Button
                                onClick={() => saveProfile.mutate()}
                                disabled={saveProfile.isPending}
                                className="bg-secondary text-secondary-foreground hover:opacity-90"
                                data-testid="profile-save-button"
                            >
                                {saveProfile.isPending ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="mr-2 h-4 w-4" />
                                )}
                                Salvar
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-xl border shadow-sm lg:col-span-1">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Foto</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <PhotoUploader
                            value={photoUrl}
                            onChange={setPhotoUrl}
                            testId="profile-photo-uploader"
                        />
                    </CardContent>
                </Card>

                <Card className="rounded-xl border shadow-sm lg:col-span-3">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold">Alterar senha</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={submitPassword} className="grid gap-4 md:grid-cols-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="cur">Senha atual</Label>
                                <Input
                                    id="cur"
                                    type="password"
                                    value={currentPass}
                                    onChange={(e) => setCurrentPass(e.target.value)}
                                    required
                                    data-testid="profile-current-password"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="newp">Nova senha</Label>
                                <Input
                                    id="newp"
                                    type="password"
                                    value={newPass}
                                    onChange={(e) => setNewPass(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="profile-new-password"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="confp">Confirmar</Label>
                                <Input
                                    id="confp"
                                    type="password"
                                    value={confirmPass}
                                    onChange={(e) => setConfirmPass(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="profile-confirm-password"
                                />
                            </div>
                            <div className="md:col-span-3">
                                <Button
                                    type="submit"
                                    disabled={changePass.isPending}
                                    className="bg-secondary text-secondary-foreground hover:opacity-90"
                                    data-testid="profile-change-password-button"
                                >
                                    {changePass.isPending ? (
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    ) : (
                                        <KeyRound className="mr-2 h-4 w-4" />
                                    )}
                                    Alterar senha
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
